import dayjs from 'dayjs';

import { ClaimMethod, ClaimStatus } from '@paid-ui/enums/claim';
import {
  ContractRequirementStatus,
  ContractRequirementType,
  ContractState,
} from '@paid-ui/enums/contract';
import { ContractPartyTag, PartyLinkingStatus, PartyRole, PayType } from '@paid-ui/enums/party';
import { ProjectIndustry } from '@paid-ui/enums/project';
import { RequestStatus } from '@paid-ui/enums/request';
import {
  ContractAction,
  RequiringActionFeatureType,
  RequiringActionType,
} from '@paid-ui/enums/requiring-action';
import { SupercontractCode } from '@paid-ui/enums/supercontract';
import { accessLinkedContractsRequestManager } from '@paid-ui/models/access-linked-contracts-request';
import { projectManager } from '@paid-ui/models/project';
import { userManager } from '@paid-ui/models/user';
import { userGroupManager } from '@paid-ui/models/user-group';
import { isContractPreAccepted } from '@paid-ui/utils/contract';

import { contractManager } from '../model';
import { checkPermission } from './check-permission';

export const savePermissions = () => {
  const { impersonation, disableContractActions } = userManager;
  const {
    templateCode,
    contractState,
    hasAllSigned,
    commencementDate,
    actualCompletionDate,
    payments,
    allContractualPartiesJoined,
    currentParty,
    lastReviewParty,
    isPayer,
    isPayee,
    isInvitee,
    inviteeParty,
    isInviter,
    isFunder,
    isQS,
    isNonContractualParty,
    retainableInfo,
    onPlatformRetainable,
    requirements,
    account,
    paymentStructure,
    requiringActions,
    statuses: {
      isPrimeContract,
      isFundingContract,
      isOffPlatformContract,
      isContractExecuted,
      isDraftContract,
      isRBNContract,
      isSuperContract,
      hasChangeFlow,
    },
  } = contractManager;
  const { permission } = userGroupManager;
  const {
    canManageContract,
    canSignContract: canUserSignContract,
    canViewContract: canUserViewContract,
  } = permission;

  const { projectIndustry } = projectManager;
  const { data: requests } = accessLinkedContractsRequestManager;

  const canSecureContractLegacy = requirements.some(
    (requirement) =>
      requirement.type === ContractRequirementType.LEGACY_SECURE_FUNDS_AGREEMENT &&
      requirement.status === ContractRequirementStatus.FULFILED,
  );
  const canSecureContractModern = requirements.some(
    (requirement) =>
      requirement.type === ContractRequirementType.SECURE_PAID_DEEDS &&
      requirement.status === ContractRequirementStatus.FULFILED,
  );
  const canSecureContract =
    !disableContractActions &&
    permission.canManagePayment &&
    (isPayer || isPayee) &&
    (canSecureContractLegacy || canSecureContractModern);

  const isContractSubmitted = contractState === ContractState.SUBMITTED && !isDraftContract;

  const isCommencementDateBeforeToday =
    !!commencementDate && dayjs(commencementDate).isBefore(dayjs().startOf('day'));

  const hasPaymentUnclaimed = payments?.some((payment) => !payment.claim);

  const hasPaymentUnclaimedOrRejected = payments.some(
    (payment) => !payment.claim || payment.claim?.state === ClaimStatus.REJECTED,
  );

  const canAbandonContract =
    !disableContractActions &&
    canManageContract &&
    isInviter &&
    !isDraftContract &&
    [ContractState.SUBMITTED, ContractState.RESUBMITTED, ContractState.REJECTED].includes(
      contractState,
    );

  const canTerminateContract =
    !disableContractActions &&
    canManageContract &&
    !isDraftContract &&
    templateCode === SupercontractCode.OFF_PLATFORM_HEAD_DEPOSIT_CONTRACT_2025 &&
    [ContractState.EXECUTED, ContractState.ALL_PAID].includes(contractState);

  const canDepositRefund =
    !disableContractActions &&
    canManageContract &&
    !isDraftContract &&
    templateCode === SupercontractCode.OFF_PLATFORM_HEAD_DEPOSIT_CONTRACT_2025 &&
    currentParty?.payType === PayType.PAYER &&
    [ContractState.EXECUTED, ContractState.ALL_PAID].includes(contractState) &&
    Number(account.balance?.available) > 0;

  const canCreateClaim =
    !disableContractActions &&
    canManageContract &&
    isPayee &&
    !isDraftContract &&
    isContractExecuted &&
    hasAllSigned &&
    hasPaymentUnclaimed;
  const canSubmitClaim =
    canManageContract && checkPermission(ContractAction.CLAIM_SUBMIT, currentParty);
  const canEditClaim =
    canManageContract && checkPermission(ContractAction.CLAIM_EDIT, currentParty);
  const canReviewClaim =
    canManageContract && checkPermission(ContractAction.CLAIM_REVIEW, currentParty);
  const canAssessClaim =
    canManageContract && checkPermission(ContractAction.CLAIM_ASSESS, currentParty);

  const canClaimRetention =
    !disableContractActions &&
    canManageContract &&
    isPayee &&
    isContractExecuted &&
    (!!retainableInfo?.claimable?.excludeGstAmount ||
      !!onPlatformRetainable.balance?.excludeGstAmount);

  const disableVariation = requirements.some(
    (requirement) => requirement.type === ContractRequirementType.DISABLED_VARIATION,
  );

  const canCreateVariation =
    !disableContractActions &&
    canManageContract &&
    !disableVariation &&
    ((isPrimeContract && isPayee) || !isPrimeContract) &&
    !isDraftContract &&
    checkPermission(ContractAction.VARIATION_SUBMIT, currentParty) &&
    isContractExecuted &&
    hasAllSigned &&
    hasPaymentUnclaimedOrRejected;

  const canResendInvitation = isPrimeContract
    ? !disableContractActions &&
      canManageContract &&
      isContractSubmitted &&
      isInviter &&
      inviteeParty?.linkingStatus === PartyLinkingStatus.UNLINKED
    : !disableContractActions &&
      canManageContract &&
      isContractSubmitted &&
      isInviter &&
      inviteeParty?.linkingStatus === PartyLinkingStatus.LINKED;

  const canReviewOnPlatformContract =
    !disableContractActions &&
    canManageContract &&
    !isOffPlatformContract &&
    !!currentParty &&
    isInvitee;

  const canReviewOffPlatformContract =
    !disableContractActions &&
    canManageContract &&
    isOffPlatformContract &&
    !!currentParty &&
    currentParty.id !== lastReviewParty?.id;

  const canReviewContract =
    !disableContractActions &&
    hasChangeFlow &&
    canManageContract &&
    (canReviewOnPlatformContract || canReviewOffPlatformContract) &&
    [ContractState.SUBMITTED, ContractState.RESUBMITTED].includes(contractState);

  const canUnjoinContract =
    !isDraftContract &&
    isContractPreAccepted(contractState) &&
    currentParty?.invitationType !== PartyRole.INVITER &&
    !!currentParty?.linkingStatus &&
    [
      PartyLinkingStatus.LINKED,
      PartyLinkingStatus.JOINED,
      PartyLinkingStatus.FILLED_INFO,
      PartyLinkingStatus.READY_TO_SIGN,
    ].includes(currentParty.linkingStatus);

  // The commencement date endpoint loads the contract as a head contract (super contracts are
  // head contracts there), so a legacy Paid or off-platform sub/supply record cannot take one.
  const canSendCommencementNotice =
    (isSuperContract || isPrimeContract) &&
    projectIndustry !== ProjectIndustry.REAL_ESTATE &&
    templateCode !== SupercontractCode.OFF_PLATFORM_HEAD_DEPOSIT_CONTRACT_2025 &&
    !disableContractActions &&
    !isDraftContract &&
    canManageContract &&
    isPayee &&
    isContractExecuted &&
    !commencementDate;

  const canSendCompletionNotice =
    projectIndustry !== ProjectIndustry.REAL_ESTATE &&
    templateCode !== SupercontractCode.OFF_PLATFORM_HEAD_DEPOSIT_CONTRACT_2025 &&
    !disableContractActions &&
    !isDraftContract &&
    canManageContract &&
    isPayee &&
    isContractExecuted &&
    !actualCompletionDate &&
    isCommencementDateBeforeToday;

  const canSignContract =
    !disableContractActions &&
    canUserSignContract &&
    isPrimeContract &&
    !isOffPlatformContract &&
    contractState === ContractState.ACCEPTED &&
    currentParty?.linkingStatus !== PartyLinkingStatus.SIGNED;

  const isPayerInFundingContract = isFundingContract && isPayer;
  const isPayerInPrimeContract = isPrimeContract && !isRBNContract && isPayer;
  const isFunderInPrimeContract = isPrimeContract && !isRBNContract && isFunder;
  const isQSInPrimeContract = isPrimeContract && isQS;

  const canViewContract = canUserViewContract && !isDraftContract;
  const canInviteFunder = !isDraftContract && isPayerInPrimeContract;
  const canInviteQS = !isDraftContract && (isPayerInFundingContract || isFunderInPrimeContract);

  const hasPendingAccessRequest = requests.some(
    (request) =>
      request.requestStatus === RequestStatus.SUBMITTED &&
      request.createdBy.groupId === currentParty?.userGroup?.id,
  );

  const canRequestLinkedContractsAccess =
    (isPayerInFundingContract ||
      isPayerInPrimeContract ||
      isFunderInPrimeContract ||
      isQSInPrimeContract) &&
    paymentStructure === ClaimMethod.PERIODIC &&
    !!currentParty &&
    !currentParty.tags?.includes(ContractPartyTag.ACCESS_LINKED_CONTRACT_GRANTED) &&
    !currentParty.tags?.includes(ContractPartyTag.ACCESS_FUNDED_HEAD_BUILDING_CONTRACT_GRANTED) &&
    [PartyLinkingStatus.JOINED, PartyLinkingStatus.SIGNED].includes(currentParty.linkingStatus) &&
    [ContractState.EXECUTED, ContractState.ALL_PAID].includes(contractState) &&
    !hasPendingAccessRequest;

  const canEditDraftContract = !disableContractActions && hasChangeFlow && isDraftContract;
  const canDeleteDraftContract = !disableContractActions && isDraftContract;

  const canRevokeContractBase =
    !disableContractActions &&
    canManageContract &&
    templateCode !== SupercontractCode.PAID_DEED_SECUREPAID_2025 &&
    contractState === ContractState.ACCEPTED &&
    !hasAllSigned &&
    !isNonContractualParty;
  const canRevokeRBNContract = canRevokeContractBase && impersonation && isRBNContract;
  const canRevokeSuperContract = canRevokeContractBase && isSuperContract;
  const canRevokeContract = canRevokeRBNContract || canRevokeSuperContract;
  const canUploadContract =
    !disableContractActions &&
    impersonation &&
    canManageContract &&
    isContractExecuted &&
    !isNonContractualParty;

  const partyActions = currentParty?.role?.actions ?? [];

  const reviewContractAction = requiringActions?.find(
    (action) =>
      action.action === RequiringActionType.REVIEW &&
      action.featureItemType === RequiringActionFeatureType.CONTRACT,
  );

  const canRecallContract =
    !disableContractActions &&
    canManageContract &&
    isSuperContract &&
    !isDraftContract &&
    [ContractState.SUBMITTED, ContractState.RESUBMITTED].includes(contractState) &&
    !hasAllSigned &&
    !isNonContractualParty &&
    !reviewContractAction &&
    (currentParty?.invitationType === PartyRole.INVITER
      ? true
      : allContractualPartiesJoined && partyActions.includes(ContractAction.CONTRACT_REVIEW));

  const canChangePrimaryContact =
    !disableContractActions && canManageContract && !isDraftContract && !isRBNContract;

  const canUpdateFeeStructure = !isDraftContract && impersonation;

  const canEditContractSignatories =
    !disableContractActions &&
    !isOffPlatformContract &&
    !isDraftContract &&
    [ContractState.SUBMITTED, ContractState.RESUBMITTED, ContractState.REJECTED].includes(
      contractState,
    );

  contractManager.permissions = {
    canSecurePayment: canSecureContractLegacy,
    canSecureContract,
    canAbandonContract,
    canTerminateContract,
    canCreateClaim,
    canAssessClaim,
    canEditClaim,
    canSubmitClaim,
    canReviewClaim,
    canCreateVariation,
    canResendInvitation,
    canReviewContract,
    canSendCommencementNotice,
    canSendCompletionNotice,
    canSignContract,
    canViewContract,
    canInviteFunder,
    canInviteQS,
    canRequestLinkedContractsAccess,
    canClaimRetention,
    canRevokeContract,
    canRecallContract,
    canEditDraftContract,
    canDeleteDraftContract,
    canDepositRefund,
    canUploadContract,
    canChangePrimaryContact,
    canUpdateFeeStructure,
    canUnjoinContract,
    canEditContractSignatories,
  };
};
